
import {
    empty as observableEmpty,
    combineLatest as observableCombineLatest,
    of as observableOf,
    Observable,
    ReplaySubject,
    Subscription
} from 'rxjs';

import {distinctUntilChanged, map, mergeMap, switchMap, take, tap} from 'rxjs/operators';
import {Inject, Injectable} from '@angular/core';
import {ProjectId, DeviceSlotId, DeviceId, MachinePoolId, AssetId, MachineId} from '../../types/ids';
import {
    isUUID, isNoUUID
} from '../../utils/utils';

import * as _ from 'lodash';
import {
    DeviceSlot, deviceSlotFromBackendObject
} from '../../types/devices/device-slot';
import {EventsService, WebmateWebsocketEvent} from '../events/events.service';
import {UserService} from '../user.service';
import {DeviceApi} from '../../api/rest/device.restapi';
import {GenericApiResponse} from '../../api/apibase.api';
import {BandwidthLimitApi} from '../../types/interfaces/bandwidth-limit.interface';

import {Machine, MachinePool, MachinePoolUpdateConfig} from "@webmate-web-core/shared/types/devices/machine";
import {StartupScript} from '@webmate-web-core/shared/types/startupscript';
import {AdminApi} from "@webmate-web-core/shared/api/rest/administration.restapi";



/**
 * Repository managing Device Slots.
 */
@Injectable({
    providedIn: 'root'
})
export class DeviceSlotService {

    //  _____ _   _ _______ ______ _____  _   _          _
    // |_   _| \ | |__   __|  ____|  __ \| \ | |   /\   | |
    //   | | |  \| |  | |  | |__  | |__) |  \| |  /  \  | |
    //   | | | . ` |  | |  |  __| |  _  /| . ` | / /\ \ | |
    //  _| |_| |\  |  | |  | |____| | \ \| |\  |/ ____ \| |____
    // |_____|_| \_|  |_|  |______|_|  \_\_| \_/_/    \_\______|


    private _slotsByProjectById: { [s: string /* ProjectId*/]: {[s: string /* DeviceSlotId*/]: ReplaySubject<DeviceSlot>}} = {}; //we cache Slots multiple times since they may differ from project to project
    private _projectCacheWrapper:  { [s: string /* ProjectId*/]: ReplaySubject<DeviceSlotId[]> } = {}; //required so that we have something we can push a 'next' to when we obtain a "DeviceSlot.AddedToProject"-Event
    private _slotEventsHandleByProject: { [s: string /* ProjectId*/]: Observable<WebmateWebsocketEvent> } = {}; //events

    /** this is a special Cache for the Administrative UseCase, it is NEVER updated by Events but can be requeried by the User */
    private _adminSlots: { [s: string]: ReplaySubject<DeviceSlot>};
    private _adminSlotsCache: {[s: string /* DeviceSlotId */]: DeviceSlot} = {};

    private _adminMachinePools: { [s: string]: ReplaySubject<MachinePool>};
    private _adminMachinePoolsCache: {[s: string /* MachineId */]: MachinePool} = {};

    private fillAdminSlotSubscription: Subscription;
    private fillAdminMachineSubscription: Subscription;

    private scriptsInProjectCache: { [s: ProjectId]: ReplaySubject<StartupScript[]>} = {};

    constructor(private deviceApi: DeviceApi, private userService: UserService, private eventsService: EventsService){}

    private getSlotsForProjectFromCache(projectId: ProjectId): Observable<DeviceSlot[]> {
        /* switchMap (not mergeMap!) is essential here: every emission of the wrapper builds a new combineLatest over
         * the per-slot subjects. With mergeMap the previous ones stayed subscribed forever, so after k updates a
         * single slot change produced k emissions - which multiplied into thousands of downstream requests
         * (e.g. the deployables queries of the workbench device view). */
        return this._projectCacheWrapper[projectId].pipe(
            distinctUntilChanged((a, b) => DeviceSlotService._sameSlotIds(a, b)),
            switchMap( deviceSlotIds => {
                let tmp: Observable<DeviceSlot>[] = deviceSlotIds.map( deviceSlotId => this._slotsByProjectById[projectId][deviceSlotId].asObservable());
                return tmp.length == 0 ? observableOf([]) : observableCombineLatest(tmp).pipe(map(arr => arr));
            })
        );
    }

    /** compares two slot-id lists irrespective of their order */
    private static _sameSlotIds(a: DeviceSlotId[], b: DeviceSlotId[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        const sortedB = b.slice().sort();
        return a.slice().sort().every((id, i) => id === sortedB[i]);
    }

    private getAdminSlotsForProjectFromCache(): Observable<DeviceSlot[]> {
        let tmp: Observable<DeviceSlot>[] = _.keys(this._adminSlots).map(deviceSlotId => this._adminSlots[deviceSlotId].asObservable());
        return tmp.length == 0 ? observableOf([]) : observableCombineLatest(tmp).pipe(map(arr => arr));
    }


    private getAdminMachinePoolsFromCache(): Observable<MachinePool[]> {
        let tmp: Observable<MachinePool>[] = _.keys(this._adminMachinePools).map(machinePoolId => this._adminMachinePools[machinePoolId].asObservable());
        return tmp.length == 0 ? observableOf([]) : observableCombineLatest(tmp).pipe(map(arr => arr));
    }


    private _registerEventsForProject(projectId: ProjectId): void{
        if (isUUID(projectId) && this._slotEventsHandleByProject[projectId] === undefined) {
            this._slotEventsHandleByProject[projectId] = this.eventsService.getEventsForProject(projectId); //register events!
            this._slotEventsHandleByProject[projectId].subscribe(
                next => DeviceSlotService._handleEvents(next, this, projectId),
                err => console.warn('DeviceSlotService - Events - should never happen!', err /* should never happen, WebSocketService should protect us! */),
                () => DeviceSlotService._handleNoMoreEvents(this, projectId)
            );
        }
    }

    /** the central event handler, makes changes (aka produces new NEXT values) in your Caches depending on the type of Event */
    private static _handleEvents(e: WebmateWebsocketEvent, instanceOfThis: any, forProject: ProjectId): void {
        if (e === undefined) return;
        if (e.eventType === 'DeviceSlot.AddedToProject') {
            if (e.deviceSlotId !== undefined) {
                instanceOfThis.obtainSlotInProjectFromBackend(e.deviceSlotId, forProject); //query the project-specific info
            }
        } else if (e.eventType === 'DeviceSlot.Updated') {
            if (instanceOfThis._adminSlots && Object.keys(instanceOfThis._adminSlots).length !== 0) {
                // refresh existing admin slot caches
                instanceOfThis.refreshAllAdminDeviceSlots();
            }
            if (e.deviceSlotId !== undefined) {
                instanceOfThis.obtainSlotInProjectFromBackend(e.deviceSlotId, forProject); //query the project-specific info
            }
        } else if (e.eventType === 'DeviceSlot.RemovedFromProject') {
            if (e.deviceSlotId !== undefined) {
                if (instanceOfThis._slotsByProjectById[forProject] && instanceOfThis._projectCacheWrapper[forProject] !== undefined){
                    let currentSlots: DeviceSlotId[] = Object.keys(instanceOfThis._slotsByProjectById[forProject]);
                    let indexOfRemovedSlot = currentSlots.indexOf(e.deviceSlotId);
                    if (indexOfRemovedSlot >= 0){
                        currentSlots.splice(indexOfRemovedSlot, 1);
                        instanceOfThis._projectCacheWrapper[forProject].next(currentSlots);
                    }
                    if (instanceOfThis._slotsByProjectById[forProject][e.deviceSlotId] !== undefined){
                        instanceOfThis._slotsByProjectById[forProject][e.deviceSlotId].complete();
                        delete instanceOfThis._slotsByProjectById[forProject][e.deviceSlotId];
                    }
                }
            }
        }

        /* else, we do not need to do anything. */
    }

    /** function that is called when the Events-Service signals that there will be no more DeviceSlot-Events */
    private static _handleNoMoreEvents(instanceOfThis: any, forProject: ProjectId): void {
        instanceOfThis.eventsHandle[forProject] = undefined;
        instanceOfThis._cleanCaches();
    }

    private obtainSlotInProjectFromBackend(slotId: DeviceSlotId, projectId: ProjectId): void {
        if (this._slotsByProjectById[projectId] === undefined) this._slotsByProjectById[projectId] = {};
        if (this._slotsByProjectById[projectId][slotId] === undefined) this._slotsByProjectById[projectId][slotId] = new ReplaySubject(1);
        if (this._projectCacheWrapper[projectId] === undefined) this._projectCacheWrapper[projectId] = new ReplaySubject(1);
        this.deviceApi.getSlotById(slotId).pipe(map(slot => {
            // TODO update admin cache if available
            this._slotsByProjectById[projectId][slotId].next(slot);
            /* we always publish the id-list: it is the only place where a slot that is known to this project but not
             * yet part of the list (e.g. because it was obtained through 'getSlotInProject$') joins it. Pushing an
             * unchanged list is free, the distinctUntilChanged in 'getSlotsForProjectFromCache' swallows it. */
            let oldIdsPlusNewOne = Object.keys(this._slotsByProjectById[projectId]);
            this._projectCacheWrapper[projectId].next(oldIdsPlusNewOne);
        })).subscribe();

    }

    refreshAdminSlotCaches(): Observable<any>{
        if (this._adminSlots === undefined) {
            this._adminSlots = {};
        }
        return this.deviceApi.getAllSlots().pipe(map(slotArr => {
            for (let s of slotArr){
                if (this._adminSlots[s.id] === undefined){
                    this._adminSlots[s.id] = new ReplaySubject(1);
                }
                this._adminSlots[s.id].next(s); //overwrite if present, our info can only be newer :-)
                this._adminSlotsCache[s.id] = s;
            }
        }));
    }

    private fillAdminMachineCaches(): Observable<any>{
        return this.deviceApi.getAllMachinePools().pipe(map(slotArr => {
            for (let s of slotArr){
                if (this._adminMachinePools[s.id] === undefined){
                    this._adminMachinePools[s.id] = new ReplaySubject(1);
                }
                this._adminMachinePools[s.id].next(s); //overwrite if present, our info can only be newer :-)
                this._adminMachinePoolsCache[s.id] = s;
            }
        }));
    }

    //   _____ ______ _______      _______ _____ ______             _____ _____
    //  / ____|  ____|  __ \ \    / /_   _/ ____|  ____|      /\   |  __ \_   _|
    // | (___ | |__  | |__) \ \  / /  | || |    | |__        /  \  | |__) || |
    //  \___ \|  __| |  _  / \ \/ /   | || |    |  __|      / /\ \ |  ___/ | |
    //  ____) | |____| | \ \  \  /   _| || |____| |____    / ____ \| |    _| |_
    // |_____/|______|_|  \_\  \/   |_____\_____|______|  /_/    \_\_|   |_____|


    /** Administrative UseCase: Returns an observable list of all the Device Slots in existence (for this customer) */
    getAllAdminSlots(): Observable<DeviceSlot[]> {
        if (this._adminSlots !== undefined) {
            return this.getAdminSlotsForProjectFromCache();
        } else {
            return this.userService.getCurrentUserData().pipe(mergeMap(weAreAuthenticated =>
                this.refreshAdminSlotCaches()
            ), mergeMap( afterwards => {
                return this.getAdminSlotsForProjectFromCache();
            }));
        }
    }

    /** Administrative UseCase: Returns an observable list of all the Mobile devices manageable for this customer */
    getAllManageableMachinePools(): Observable<MachinePool[]> {
        if (this._adminMachinePools !== undefined) {
            return this.getAdminMachinePoolsFromCache();
        } else {
            this._adminMachinePools = {};
            return this.userService.getCurrentUserData().pipe(mergeMap(weAreAuthenticated =>
                this.fillAdminMachineCaches()
            ), mergeMap( afterwards => {
                return this.getAdminMachinePoolsFromCache();
            }));
        }
    }

    getAdminSlotsForProject(projectId: ProjectId): Observable<DeviceSlot[]> {
        return this.getAllAdminSlots().pipe(map(slots => slots.filter(s => s.projects.includes(projectId))));
    }


    /** Returns an observable list of Ids of all the Device Slots that are available in the given Project. Note: Queries full Slots from the Backend if necessary. */
    getAvailableSlotIdsForProject$(projectId: ProjectId): Observable<DeviceSlotId[]> {
        return this.getAvailableSlotsForProject$(projectId).pipe(map( slots => slots.map( d => d.id)));
    }

    /** Returns an observable list of all the Device Slots that are available in the given Project. */
    getAvailableSlotsForProject$(projectId: ProjectId): Observable<DeviceSlot[]> {
        if (isNoUUID(projectId)) throw new ReferenceError('\'getAvailableSlotsForProject$\' called with an illegal ID: \''+projectId+'\'');
        if (this._projectCacheWrapper[projectId] !== undefined){
            return this.getSlotsForProjectFromCache(projectId);
        } else {
            return this.deviceApi.getSlotsForProject(projectId).pipe(map(slotArr => {
                if (this._projectCacheWrapper[projectId] === undefined) {
                    this._projectCacheWrapper[projectId] = new ReplaySubject(1);
                }
                if (this._slotsByProjectById[projectId] === undefined) {
                    this._slotsByProjectById[projectId] = {};
                }
                let idsOnly = [];

                for (let slot of slotArr){
                    if (this._slotsByProjectById[projectId][slot.id] === undefined){
                        this._slotsByProjectById[projectId][slot.id] = new ReplaySubject(1);
                    }
                    idsOnly.push(slot.id);
                    this._registerEventsForProject(projectId); //we need to listen to events!
                    this._slotsByProjectById[projectId][slot.id].next(slot); //overwrite if present, our info can only be newer :-)
                }
                this._projectCacheWrapper[projectId].next(idsOnly);
            }), mergeMap(() => this.getSlotsForProjectFromCache(projectId)));
        }
    }

    /** returns an Observable that is EMPTY and fires NO NEXT VALUE unless there is a Slot in the given Project that contains the given DEVICE! */
    getSlotForDeviceInProject(deviceId: DeviceId, projectId: ProjectId): Observable<DeviceSlot> {
        return this.getAvailableSlotsForProject$(projectId).pipe(mergeMap( slots => {
            let slot: DeviceSlot | undefined = slots.find( s => s.deviceId === deviceId);
            return slot === undefined ? observableEmpty() : observableOf(slot);
        }));
    }

    /** Returns the specified Device Slot by the given Id */
    getSlotInProject$(id: DeviceSlotId, projectId: ProjectId): Observable<DeviceSlot> {
        if (isNoUUID(id)) throw new ReferenceError('\'getSlotInProject$\' called with an illegal SlotId: \'' + id + '\'');
        if (isNoUUID(projectId)) throw new ReferenceError('\'getSlotInProject$\' called with an illegal ProjectId: \'' + projectId + '\'');
        if (this._slotsByProjectById[projectId] !== undefined && this._slotsByProjectById[projectId][id] !== undefined) {
            return this._slotsByProjectById[projectId][id].asObservable();
        } else {
            if (this._slotsByProjectById[projectId] === undefined) this._slotsByProjectById[projectId] = {};
            if (this._slotsByProjectById[projectId][id] === undefined) this._slotsByProjectById[projectId][id] = new ReplaySubject(1);
            return this.userService.getCurrentUserData().pipe(mergeMap(weAreAuthenticated =>
                this.deviceApi.getSlotById(id).pipe(map(s => {
                    this._registerEventsForProject(projectId); //we need to listen to events!
                    this._slotsByProjectById[projectId][id].next(s);
                    return s;
                }))),mergeMap(afterwards => {
                return this._slotsByProjectById[projectId][id].asObservable();
            }),);
        }
    }

    /** Returns the specified Device Slot by the given Id, this is an ADMINISTRATIVE USE CASE as the Slot will contain raw data! */
    getSlotForAdministrativePurposes$(id: DeviceSlotId): Observable<DeviceSlot> {
        if (isNoUUID(id)) throw new ReferenceError('\'getSlot$\' called with an illegal ID: \'' + id + '\'');
        if (this._adminSlots !== undefined && this._adminSlots[id] !== undefined) {
            return this._adminSlots[id].asObservable();
        } else {
            return this.getAllAdminSlots().pipe(map(slots => slots.find(s => s.id === id)));
        }
    }

    setProjectsForSlot(slotId: DeviceSlotId, projects: ProjectId[]): Observable<GenericApiResponse> {
        return this.deviceApi.setProjectsForSlot(slotId, projects).pipe(tap(res => {
            this.refreshAllAdminDeviceSlots();
        }));
    }

    updateAutoRedeploymentOfSlotInProject(projectId: ProjectId, deviceSlotId: DeviceSlotId, newValue: boolean): Observable<GenericApiResponse>{
        return this.deviceApi.updateAutoRedeployment(deviceSlotId, projectId, newValue);
    }

    setSlotProperties(slotId: DeviceSlotId, name: string): Observable<GenericApiResponse>{
        return this.deviceApi.setSlotProperties(slotId, name).pipe(tap(res => {
            this._adminSlotsCache[slotId].name = name;
            this._adminSlots[slotId].next(this._adminSlotsCache[slotId]);
        }));
    }

    clearSlot(slotId: DeviceSlotId): Observable<GenericApiResponse> {
        return this.deviceApi.clearSlot(slotId).pipe(tap(res => {
            // only manually update the admin caches here. the regular caches should be updated automatically by events later
            if (this._adminSlotsCache[slotId] !== undefined) {
                this._adminSlotsCache[slotId].deviceId = undefined;
                this._adminSlots[slotId].next(this._adminSlotsCache[slotId]);
            }
        }));
    }

    refreshAllAdminDeviceSlots(): void {
        if (this.fillAdminSlotSubscription) this.fillAdminSlotSubscription.unsubscribe();
        this.fillAdminSlotSubscription = this.refreshAdminSlotCaches().pipe(take(1)).subscribe();
    }

    setMachinePoolUpdateConfig(poolId: MachinePoolId, machinePoolUpdateConfig: MachinePoolUpdateConfig): Observable<GenericApiResponse> {
        return this.deviceApi.setMachinePoolUpdateConfig(poolId, machinePoolUpdateConfig).pipe(tap(res => {
            this.refreshAllMachinePools();
        }, error => {
            this.refreshAllMachinePools();
        }));
    }

    setMachineAnnotations(machineId: MachineId, machinePoolId: MachinePoolId, msg: string): Observable<GenericApiResponse> {
        return this.deviceApi.setMachineAnnotations(machineId, machinePoolId, msg).pipe(tap(res => {
            this.refreshAllMachinePools();
        }, error => {
            this.refreshAllMachinePools();
        }));
    }

    deleteMachineAnnotations(machineId: MachineId, machinePoolId: MachinePoolId): Observable<GenericApiResponse> {
        return this.deviceApi.deleteMachineAnnotations(machineId, machinePoolId).pipe(tap(res => {
            this.refreshAllMachinePools();
        }, error => {
            this.refreshAllMachinePools();
        }));
    }

    refreshAllMachinePools(): void {
        if (this.fillAdminMachineSubscription) this.fillAdminMachineSubscription.unsubscribe();
        this.fillAdminMachineSubscription = this.fillAdminMachineCaches().subscribe();
    }

    getScriptsInProject(projectId: ProjectId, forceReload: boolean): Observable<Array<StartupScript>> {
        if (!forceReload && this.scriptsInProjectCache[projectId]) {
            return this.scriptsInProjectCache[projectId].asObservable();
        }
        return this.deviceApi.getScriptsInProject(projectId).pipe(mergeMap(scripts => {
            if (!this.scriptsInProjectCache[projectId]) {
                this.scriptsInProjectCache[projectId] = new ReplaySubject(1);
            }
            this.scriptsInProjectCache[projectId].next(scripts);
            return this.scriptsInProjectCache[projectId];
        }));
    }

    addScriptToSlot(assetId: AssetId, slotId: DeviceSlotId) {
        return this.deviceApi.addScriptToSlot(assetId, slotId).pipe(tap(() => {
            if (this._adminSlots) {
                this.refreshAllMachinePools();
            }
        }));
    }

    removeScriptFromSlot(assetId: AssetId, slotId: DeviceSlotId) {
        return this.deviceApi.removeScriptFromSlot(assetId, slotId).pipe(tap(() => {
            if (this._adminSlots) {
                this.refreshAllMachinePools();
            }
        }));
    }

    /** reports malfunction of a Slot */
    sendMalfunctionReportOfSlot$(slotId: DeviceSlotId, title: string, message: string, projectId: ProjectId): Observable<boolean> {
        if (isNoUUID(slotId)) throw new ReferenceError("'sendMalfunctionReportOfSlot$' called with an illegal ID: '" + slotId + "'");
        return this.deviceApi.sendMalfunctionReportOfSlot(slotId, title, message, projectId).pipe(map(success => true, error => false));
    }


    downloadInfoAsCSV(): Observable<Blob> {
        return this.deviceApi.downloadSlotInfoAsCSV();
    }
}
